import { useState, useEffect } from 'react';
import {
  PencilIcon,
  TrashIcon,
  PlusIcon,
  CheckCircleIcon,
  XCircleIcon,
  XMarkIcon,
  PhotoIcon,
} from '@heroicons/react/24/outline';
import { getLogos, createLogo, updateLogo, deleteLogo } from '../../lib/supabase';
import type { Logo } from '../../types/content';
import type { LogoData } from '../../lib/validation';
import { toast } from 'react-hot-toast';

const LOGO_TYPES = ['primary', 'secondary', 'favicon'] as const;
const LOGO_LOCATIONS = ['header', 'footer'] as const;
const LOGO_FORMATS = ['svg', 'png', 'ico'] as const;

const emptyForm = (): LogoData => ({
  name: '',
  url: '',
  type: 'primary',
  location: 'header',
  format: 'png',
  width: undefined,
  height: undefined,
  is_active: false,
});

export default function MobileLogoManager() {
  const [logos, setLogos] = useState<Logo[]>([]);
  const [loading, setLoading] = useState(true);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editing, setEditing] = useState<Logo | null>(null);
  const [form, setForm] = useState<LogoData>(emptyForm());
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Logo | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchLogos();
  }, []);

  const fetchLogos = async () => {
    try {
      setLoading(true);
      const { data, error } = await getLogos();
      if (error) throw error;
      setLogos(data || []);
    } catch {
      toast.error('Failed to load logos');
    } finally {
      setLoading(false);
    }
  };

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm());
    setSheetOpen(true);
  };

  const openEdit = (logo: Logo) => {
    setEditing(logo);
    setForm({
      name: logo.name,
      url: logo.url,
      type: logo.type,
      location: logo.location,
      format: logo.format,
      width: logo.width,
      height: logo.height,
      is_active: logo.is_active,
    });
    setSheetOpen(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      if (editing) {
        const { error } = await updateLogo(editing.id, form);
        if (error) throw error;
      } else {
        const { error } = await createLogo(form);
        if (error) throw error;
      }
      await fetchLogos();
      setSheetOpen(false);
      toast.success(editing ? 'Logo updated' : 'Logo added');
    } catch (err: any) {
      toast.error(err.message || 'Failed to save logo');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (logo: Logo) => {
    try {
      const { error } = await updateLogo(logo.id, { ...logo, is_active: !logo.is_active });
      if (error) throw error;
      await fetchLogos();
      toast.success(logo.is_active ? 'Deactivated' : 'Activated');
    } catch {
      toast.error('Failed to update logo');
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      const { error } = await deleteLogo(deleteTarget.id);
      if (error) throw error;
      await fetchLogos();
      setDeleteTarget(null);
      toast.success('Logo deleted');
    } catch {
      toast.error('Failed to delete logo');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-4 border-golf-orange border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="px-4 py-4 space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest">
          {logos.length} logo{logos.length !== 1 ? 's' : ''}
        </p>
        <button
          onClick={openAdd}
          className="flex items-center gap-1.5 px-3 py-2 bg-golf-orange text-white text-xs font-semibold rounded-xl active:bg-golf-orange/90 transition-colors"
        >
          <PlusIcon className="w-3.5 h-3.5" />
          Add Logo
        </button>
      </div>

      {logos.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm px-4 py-10 text-center">
          <PhotoIcon className="w-8 h-8 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-400">No logos yet</p>
          <button onClick={openAdd} className="mt-2 text-xs text-golf-orange font-medium">
            Add the first logo
          </button>
        </div>
      ) : (
        logos.map(logo => (
          <div key={logo.id} className={`bg-white rounded-2xl border border-gray-100 shadow-sm px-4 py-3.5 ${!logo.is_active ? 'opacity-60' : ''}`}>
            <div className="flex items-center gap-3">
              {logo.url ? (
                <img src={logo.url} alt={logo.name} className="w-10 h-10 object-contain rounded-lg bg-gray-50 border border-gray-100 flex-shrink-0" />
              ) : (
                <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                  <PhotoIcon className="w-5 h-5 text-gray-400" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-golf-gray truncate">{logo.name}</p>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <span className="text-xs text-gray-400 capitalize">{logo.type}</span>
                  <span className="text-gray-300">·</span>
                  <span className="text-xs text-gray-400 capitalize">{logo.location}</span>
                  <span className="text-gray-300">·</span>
                  <span className="text-xs text-gray-400 uppercase">{logo.format}</span>
                </div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  onClick={() => handleToggleActive(logo)}
                  className={`w-7 h-7 flex items-center justify-center rounded-lg transition-colors ${
                    logo.is_active ? 'bg-green-50 active:bg-green-100' : 'bg-gray-100 active:bg-gray-200'
                  }`}
                >
                  {logo.is_active
                    ? <CheckCircleIcon className="w-4 h-4 text-green-600" />
                    : <XCircleIcon className="w-4 h-4 text-gray-400" />
                  }
                </button>
                <button
                  onClick={() => openEdit(logo)}
                  className="w-7 h-7 flex items-center justify-center rounded-lg bg-blue-50 active:bg-blue-100 transition-colors"
                >
                  <PencilIcon className="w-3.5 h-3.5 text-blue-600" />
                </button>
                <button
                  onClick={() => setDeleteTarget(logo)}
                  className="w-7 h-7 flex items-center justify-center rounded-lg bg-red-50 active:bg-red-100 transition-colors"
                >
                  <TrashIcon className="w-3.5 h-3.5 text-red-500" />
                </button>
              </div>
            </div>
          </div>
        ))
      )}

      {sheetOpen && (
        <div className="fixed inset-x-0 top-0 bottom-20 z-40 flex flex-col justify-end">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setSheetOpen(false)} />
          <div className="relative bg-white rounded-t-3xl shadow-2xl max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-gray-100 flex-shrink-0">
              <div>
                <h2 className="text-base font-bold text-golf-gray">{editing ? 'Edit Logo' : 'Add Logo'}</h2>
                <p className="text-xs text-gray-400 mt-0.5">Logo details</p>
              </div>
              <button onClick={() => setSheetOpen(false)} className="w-8 h-8 flex items-center justify-center rounded-xl bg-gray-100 active:bg-gray-200 transition-colors">
                <XMarkIcon className="w-4 h-4 text-gray-500" />
              </button>
            </div>

            <form onSubmit={handleSave} className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Name</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g. Primary Logo"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-golf-orange bg-gray-50"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">URL</label>
                <input
                  type="url"
                  value={form.url}
                  onChange={e => setForm(prev => ({ ...prev, url: e.target.value }))}
                  placeholder="https://..."
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-golf-orange bg-gray-50"
                  required
                />
              </div>

              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Type</label>
                  <select
                    value={form.type}
                    onChange={e => setForm(prev => ({ ...prev, type: e.target.value as Logo['type'] }))}
                    className="w-full px-2 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-golf-orange bg-gray-50 appearance-none"
                  >
                    {LOGO_TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Location</label>
                  <select
                    value={form.location}
                    onChange={e => setForm(prev => ({ ...prev, location: e.target.value as Logo['location'] }))}
                    className="w-full px-2 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-golf-orange bg-gray-50 appearance-none"
                  >
                    {LOGO_LOCATIONS.map(l => <option key={l} value={l}>{l.charAt(0).toUpperCase() + l.slice(1)}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Format</label>
                  <select
                    value={form.format}
                    onChange={e => setForm(prev => ({ ...prev, format: e.target.value as Logo['format'] }))}
                    className="w-full px-2 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-golf-orange bg-gray-50 appearance-none"
                  >
                    {LOGO_FORMATS.map(f => <option key={f} value={f}>{f.toUpperCase()}</option>)}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Width (px)</label>
                  <input
                    type="number"
                    value={form.width ?? ''}
                    onChange={e => setForm(prev => ({ ...prev, width: e.target.value ? parseInt(e.target.value) : undefined }))}
                    placeholder="Optional"
                    className="w-full px-3 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-golf-orange bg-gray-50"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Height (px)</label>
                  <input
                    type="number"
                    value={form.height ?? ''}
                    onChange={e => setForm(prev => ({ ...prev, height: e.target.value ? parseInt(e.target.value) : undefined }))}
                    placeholder="Optional"
                    className="w-full px-3 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-golf-orange bg-gray-50"
                  />
                </div>
              </div>

              <div className="flex items-center gap-3 pb-2">
                <button
                  type="button"
                  onClick={() => setForm(prev => ({ ...prev, is_active: !prev.is_active }))}
                  className={`w-10 h-6 rounded-full transition-colors ${form.is_active ? 'bg-golf-orange' : 'bg-gray-300'}`}
                >
                  <span className={`block w-4 h-4 bg-white rounded-full shadow transition-transform mx-1 ${form.is_active ? 'translate-x-4' : ''}`} />
                </button>
                <span className="text-sm text-gray-600">Active</span>
              </div>
            </form>

            <div className="flex gap-3 px-5 py-4 border-t border-gray-100 flex-shrink-0">
              <button
                type="button"
                onClick={() => setSheetOpen(false)}
                className="flex-1 py-3 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 bg-white active:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                onClick={handleSave}
                disabled={saving}
                className="flex-1 py-3 rounded-xl bg-golf-orange text-white text-sm font-semibold active:bg-golf-orange/90 disabled:opacity-60 transition-colors"
              >
                {saving ? 'Saving...' : editing ? 'Save Changes' : 'Add Logo'}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-end">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDeleteTarget(null)} />
          <div className="relative w-full bg-white rounded-t-3xl px-5 py-6 space-y-4 shadow-2xl">
            <h3 className="text-base font-bold text-golf-gray">Delete Logo?</h3>
            <p className="text-sm text-gray-500">
              Are you sure you want to delete <span className="font-semibold text-golf-gray">"{deleteTarget.name}"</span>? This cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                className="flex-1 py-3 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 bg-white active:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={deleting}
                className="flex-1 py-3 rounded-xl bg-red-500 text-white text-sm font-semibold active:bg-red-600 disabled:opacity-60"
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
